import { useSelector } from 'react-redux';

/**
 * Feature flag value type - can be boolean or object with accessAllowed/checked
 */
type FeatureFlagValue = boolean | { accessAllowed?: boolean; checked?: boolean } | null;

/**
 * Redux state type for feature flags
 */
interface FeatureFlagState {
    ui?: {
        featureFlag?: Record<string, any>;
    };
}

/**
 * Return type for useFeatureFlags hook
 */
export interface UseFeatureFlagsReturn {
    hasFeatureFlag: (flagPath: string) => boolean;
    isFeatureEnabled: (flagPath: string) => boolean;
    getFeatureFlag: (flagPath: string) => any;
    hasAnyFeatureFlag: (flagPaths: string[]) => boolean;
    hasAllFeatureFlags: (flagPaths: string[]) => boolean;
}

/**
 * Hook for checking feature flags from Redux store
 * Supports nested feature flags (e.g., 'dashboardV5.accessAllowed')
 */
export const useFeatureFlags = (): UseFeatureFlagsReturn => {
    const featureFlags = useSelector((state: FeatureFlagState) => state.ui?.featureFlag || {});

    /**
     * Get feature flag value by path (supports nested paths)
     * @param flagPath - Dot-separated path (e.g., 'dashboardV5.accessAllowed')
     * @returns The feature flag value or null if not found
     */
    const getFeatureFlag = (flagPath: string): any => {
        if (!flagPath) return null;

        const parts = flagPath.split('.');
        let value: any = featureFlags;

        for (const part of parts) {
            if (value && typeof value === 'object') {
                value = value[part];
            } else {
                return null;
            }
        }

        return value || null;
    };

    /**
     * Check if a feature flag is enabled
     * Returns true if:
     * - The flag value is true
     * - The flag has accessAllowed === true
     * - The flag has checked === true and accessAllowed === true
     */
    const hasFeatureFlag = (flagPath: string): boolean => {
        const value = getFeatureFlag(flagPath);

        if (value === true) return true;
        if (value && typeof value === 'object') {
            return value.accessAllowed === true || (value.checked === true && value.accessAllowed === true);
        }

        return false;
    };

    /**
     * Alias for hasFeatureFlag
     */
    const isFeatureEnabled = (flagPath: string): boolean => {
        return hasFeatureFlag(flagPath);
    };

    /**
     * Check if user has any of the specified feature flags (OR logic)
     */
    const hasAnyFeatureFlag = (flagPaths: string[]): boolean => {
        if (!flagPaths || flagPaths.length === 0) return true;
        return flagPaths.some(hasFeatureFlag);
    };

    /**
     * Check if user has all of the specified feature flags (AND logic)
     */
    const hasAllFeatureFlags = (flagPaths: string[]): boolean => {
        if (!flagPaths || flagPaths.length === 0) return true;
        return flagPaths.every(hasFeatureFlag);
    };

    return {
        hasFeatureFlag,
        isFeatureEnabled,
        getFeatureFlag,
        hasAnyFeatureFlag,
        hasAllFeatureFlags,
    };
};
